import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

export type RetryRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

export type AuthRefreshError = {
  config?: RetryRequestConfig;
  response?: { status?: number; data?: { code?: string } };
};

/**
 * Sent by the API with every 401 raised while authenticating an access token.
 *
 * Refreshing is gated on this code rather than on the status alone: a 401 a
 * route raises for its own reasons is not a stale access token, and refreshing
 * for it would rotate the session's refresh token as a side effect of an
 * unrelated failure.
 */
const ACCESS_TOKEN_EXPIRED_CODE = "ACCESS_TOKEN_EXPIRED";

type AuthRefreshDependencies<Result> = {
  // The access token currently in use. Lets a request whose refresh was
  // denied retry with a session that replaced it meanwhile (see below).
  getAccessToken?: () => string | null;
  getRefreshToken: () => string | null;
  onMissingRefreshToken?: (error: AuthRefreshError) => void;
  onRefreshDenied: (error: unknown) => void | Promise<void>;
  onRefreshSucceeded: (tokens: AuthTokens) => void | Promise<void>;
  refresh: (refreshToken: string) => Promise<AuthTokens>;
  retry: (config: RetryRequestConfig) => Promise<Result>;
};

const isExplicitRefreshDenial = (error: unknown) => {
  const status = (error as AuthRefreshError)?.response?.status;
  return status === 400 || status === 401 || status === 403;
};

export const createAuthRefreshHandler = <Result>({
  getAccessToken,
  getRefreshToken,
  onMissingRefreshToken,
  onRefreshDenied,
  onRefreshSucceeded,
  refresh,
  retry,
}: AuthRefreshDependencies<Result>) => {
  let refreshInFlight: Promise<AuthTokens> | null = null;

  return async (error: AuthRefreshError): Promise<Result> => {
    const status = error.response?.status;
    const originalRequest = error.config ?? {};

    if (
      status !== 401 ||
      error.response?.data?.code !== ACCESS_TOKEN_EXPIRED_CODE ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest._retry
    ) {
      throw error;
    }

    originalRequest._retry = true;
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      onMissingRefreshToken?.(error);
      throw error;
    }

    const retryWith = (accessToken: string) =>
      retry({
        ...originalRequest,
        headers: {
          ...originalRequest.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      });

    if (!refreshInFlight) {
      const refreshedWith = refreshToken;
      refreshInFlight = refresh(refreshedWith)
        .then(async (tokens) => {
          await onRefreshSucceeded(tokens);
          return tokens;
        })
        .catch(async (refreshError) => {
          // A denial only ends the session if it is still the one that was
          // denied. A password change revokes every session and mints a new
          // one, so a request already in flight with the old access token can
          // 401 and have its refresh denied after the new session has landed
          // — signing out then would throw away that new session.
          if (
            isExplicitRefreshDenial(refreshError) &&
            getRefreshToken() === refreshedWith
          ) {
            await onRefreshDenied(refreshError);
          }
          throw refreshError;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }

    let tokens: AuthTokens;
    try {
      tokens = await refreshInFlight;
    } catch (refreshError) {
      const currentAccessToken = getAccessToken?.();
      if (currentAccessToken && getRefreshToken() !== refreshToken) {
        return retryWith(currentAccessToken);
      }
      throw refreshError;
    }

    return retryWith(tokens.accessToken);
  };
};

type AuthRefreshInterceptorDependencies = Omit<
  AuthRefreshDependencies<AxiosResponse>,
  "retry"
>;

export const installAuthRefreshInterceptor = (
  instance: AxiosInstance,
  dependencies: AuthRefreshInterceptorDependencies
) =>
  instance.interceptors.response.use(
    (response) => response,
    createAuthRefreshHandler({
      ...dependencies,
      retry: (config) => instance.request(config),
    })
  );
